import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  Button,
  ButtonLink,
  ButtonRound,
  CloseIcon,
  Column,
  DocAddIcon,
  FormBox,
  InputCEP,
  InputCheckbox,
  InputCnpj,
  InputDragDrop,
  InputPhoneNumber,
  InputText,
  Notice,
  PageActions,
  Row,
  SelectorPageStatus,
  StatePill,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { apiFileUrl } from "@helpers/apiFile";
import {
  LOGO_ACCEPT,
  companyFromApi,
  companyPayload,
  logoFileError,
  normalizeHex,
  readFileAsDataUrl,
  themeNotices,
  validateColors,
  validateCompanyData,
} from "@helpers/companyConfig";
import { TenantLogo, applyTenantTheme, deriveTenantTheme, usePageHeader } from "@components/ow";
import { FormPage, FormPageSkeleton, FullWidth, focusFirstError } from "../../Common/FormPage";
import { useUpdateCompanyData } from "../../../Layouts/CompanyDataContext";

// Dados da empresa (só ADM): cadastro e endereço na aba Dados, logo e cores na aba
// Logo e cores, com a prévia do tema. Cada aba salva o que é dela, sobre o que já
// está salvo: o PUT exige nome e CNPJ e apaga o endereço que não vier.

const BREADCRUMB = [{ page: "Dados da empresa", route: "/config" }];

const TABS = [
  { label: "Dados", value: "data" },
  { label: "Logo e cores", value: "brand" },
];

const DATA_FIELDS = ["company-name", "company-cnpj", "company-phone", "company-zip"];

const PREVIEW_TABS = [
  { label: "Dados", value: "dados" },
  { label: "Pagamentos", value: "pagamentos" },
];

const EMPTY_LOGOS = { small: null, normal: null };

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Line = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const ColorRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;

  > :last-child {
    flex: 1;
    min-width: 0;
  }
`;

// Amostra da cor: abre o seletor nativo, que fica invisível por cima dela
const Swatch = styled.label`
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  margin-top: 10px;
  border-radius: 8px;
  border: 1px ${({ $empty }) => ($empty ? "dashed" : "solid")} ${({ theme }) => theme.colors.N200};
  background: ${({ $color }) => $color || "transparent"};
  cursor: pointer;

  &:focus-within {
    outline: 2px solid var(--ow-focus-ring-color);
    outline-offset: 2px;
  }

  input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
`;

const LogoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LogoCurrent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 56px;
  padding: 12px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.N50};
`;

// Cartão do logo escolhido: miniatura, nome (abre a prévia) e desistir
const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.N100};
  border-radius: 10px;
`;

const FileName = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const Thumb = styled.img`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  object-fit: contain;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.N50};
`;

// A área da lib tem 250px de altura mínima: dois logos lado a lado ficariam altos demais
const CompactDrop = styled.div`
  > div {
    min-height: 120px;
    padding: 16px 24px;
  }
`;

const DropIcon = styled(DocAddIcon)`
  width: 32px;
  height: 32px;
`;

// Prévia do tema: as vars --ow-* da empresa só neste contêiner (applyTenantTheme)
const Preview = styled.div`
  display: flex;
  width: 100%;
  min-height: 220px;
  overflow: hidden;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.N100};
  background: ${({ theme }) => theme.colors.S50};
`;

// Faixa do menu lateral, como o Sidebar da lib
const PreviewRail = styled.div`
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  width: 64px;
  padding-top: 16px;
  background: ${({ theme }) => theme.colors.N0};
  border-right: 1px solid ${({ theme }) => theme.colors.N100};
`;

const PreviewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-width: 0;
  padding: 20px;
`;

function ColorField({ id, label, tooltip, value, error, onChange }) {
  const hex = normalizeHex(value);
  return (
    <ColorRow>
      <Swatch $color={hex || null} $empty={!hex} title={`Escolher a ${label.toLowerCase()}`}>
        <input
          type="color"
          aria-label={`Escolher a ${label.toLowerCase()}`}
          value={hex || "#FFFFFF"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
        />
      </Swatch>
      <InputText
        $idForm={id}
        $label={label}
        $variant="default"
        $hasTooltip={tooltip}
        $errors={error}
        placeholder="Padrão OneWork"
        maxLength={7}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </ColorRow>
  );
}

function LogoField({ id, label, hint, kind, currentSrc, companyName, logo, onChange }) {
  const { file, preview, error } = logo || {};
  // A área da lib lista o arquivo que recebeu (com ✓ quando o tipo passa): no recusado
  // por tamanho, remonta vazia e fica só o erro embaixo
  const [dropKey, setDropKey] = useState(0);
  return (
    <LogoBlock>
      <Text $text={label} $variant="paragraph_small_bold" $color="N700" />
      <Text $text={hint} $variant="caption_medium" $color="N400" />
      {file ? (
        <FileItem>
          <FileName>
            <Thumb src={preview} alt="" />
            <ButtonLink $text={file.name} $action={() => window.open(preview, "_blank", "noopener")} $noPadding $noWidth />
          </FileName>
          <ButtonRound
            title="Desistir deste logo"
            aria-label="Desistir deste logo"
            $variant="outline"
            $icon={<CloseIcon />}
            $noWidth
            $action={() => onChange(null)}
          />
        </FileItem>
      ) : (
        <>
          <LogoCurrent>
            <TenantLogo kind={kind} src={currentSrc} companyName={companyName} />
            <Text
              $text={currentSrc ? "Logo atual" : "Sem logo: aparece o do OneWork"}
              $variant="caption_medium"
              $color="N500"
            />
          </LogoCurrent>
          <CompactDrop id={id} key={dropKey}>
            <InputDragDrop
              accept={LOGO_ACCEPT}
              maxFiles={1}
              typeFile={{ valid: "image/png", message: " · PNG, JPG ou WEBP, até 1 MB" }}
              $icon={<DropIcon />}
              $message={currentSrc ? "Arraste o logo novo ou <strong>clique para trocar</strong>" : "Arraste o logo ou <strong>clique para escolher</strong>"}
              onDrop={(accepted, rejected) => {
                const picked = accepted[0] || rejected[0]?.file;
                if (!picked) return;
                const message = logoFileError(picked);
                if (message) setDropKey((key) => key + 1);
                onChange(message ? { error: message } : { file: picked, preview: URL.createObjectURL(picked) });
              }}
            />
          </CompactDrop>
        </>
      )}
      {error ? <Text $text={error} $variant="caption_medium" $color="E400" /> : null}
    </LogoBlock>
  );
}

function ThemePreview({ colors, logoSrc, companyName }) {
  const ref = useRef(null);
  const theme = useMemo(
    () => ({ primary: normalizeHex(colors.primary) || undefined, secondary: normalizeHex(colors.secondary) || undefined }),
    [colors.primary, colors.secondary]
  );
  const [tab, setTab] = useState(PREVIEW_TABS[0]);

  useEffect(() => {
    if (ref.current) applyTenantTheme(ref.current, theme);
  }, [theme]);

  const notices = themeNotices(deriveTenantTheme(theme), theme);

  return (
    <Stack>
      <Preview ref={ref} aria-label="Prévia do tema">
        <PreviewRail>
          <TenantLogo kind="symbol" src={logoSrc} companyName={companyName} />
        </PreviewRail>
        <PreviewContent>
          <Text $text="Contrato · Projeto" $variant="h4_bold" $color="N900" />
          <SelectorPageStatus $variant="segmented" options={PREVIEW_TABS} selected={tab} onChange={setTab} />
          <Line>
            <Button $text="Salvar" $variant="primary" $action={() => {}} $noWidth />
            <Button $text="Cancelar" $variant="outline" $action={() => {}} $noWidth />
          </Line>
          <Line>
            <ButtonLink $text="Ver detalhes" $action={() => {}} $hasIcon />
            <StatePill $text="Aprovado" $variant="green" />
          </Line>
        </PreviewContent>
      </Preview>
      {notices.map((text) => (
        <Notice key={text} $variant="gray" $text={text} />
      ))}
      <Text
        $text="A cor dos status (verde de aprovado, vermelho de reprovado) não muda com a empresa."
        $variant="caption_medium"
        $color="N400"
      />
    </Stack>
  );
}

export default function Config() {
  const companyService = useService("company");
  const toolsService = useService("tools");
  const updateCompanyData = useUpdateCompanyData();

  const [tab, setTab] = useState(TABS[0]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [data, setData] = useState(null);
  const [colors, setColors] = useState(null);
  const [logos, setLogos] = useState(EMPTY_LOGOS);
  const [saving, setSaving] = useState(false);
  // Erros nos campos depois da primeira tentativa, atualizados a cada mudança
  const [dataSubmitted, setDataSubmitted] = useState(false);
  const [colorsSubmitted, setColorsSubmitted] = useState(false);
  const [zipError, setZipError] = useState("");
  const [zipLoading, setZipLoading] = useState(false);
  // O InputCheckbox da lib não é controlado: remonta ao descartar
  const [checkboxKey, setCheckboxKey] = useState(0);
  const lastZipRef = useRef("");

  usePageHeader({ title: "Dados da empresa", breadcrumb: BREADCRUMB });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // As prévias dos logos escolhidos são URLs do navegador: liberadas ao trocar ou sair
  const logosRef = useRef(logos);
  logosRef.current = logos;
  useEffect(() => () => Object.values(logosRef.current).forEach(revokePreview), []);

  function revokePreview(logo) {
    if (logo?.preview) URL.revokeObjectURL(logo.preview);
  }

  function changeLogo(kind, logo) {
    revokePreview(logos[kind]);
    setLogos((prev) => ({ ...prev, [kind]: logo }));
  }

  function resetLogos() {
    Object.values(logos).forEach(revokePreview);
    setLogos(EMPTY_LOGOS);
  }

  async function load() {
    const response = await companyService.getCompanyData();
    if (!response || response.error || !response.data) {
      toast.error("Não foi possível carregar os dados da empresa");
      setLoading(false);
      return;
    }
    applySaved(companyFromApi(response.data));
    setCompanyId(response.data.id);
    setLoading(false);
  }

  function applySaved(company) {
    setSaved(company);
    setData(company.data);
    setColors(company.colors);
    lastZipRef.current = company.data.zip_code.replace(/\D/g, "");
  }

  const dataErrors = data && dataSubmitted ? validateCompanyData(data, saved.data) : {};
  const colorErrors = colors && colorsSubmitted ? validateColors(colors) : {};
  const dataDirty = !!data && JSON.stringify(data) !== JSON.stringify(saved.data);
  const brandDirty =
    !!colors &&
    (Object.keys(colors).some((key) => (normalizeHex(colors[key]) ?? colors[key]) !== saved.colors[key]) ||
      !!logos.small?.file ||
      !!logos.normal?.file);

  const setField = (field) => (e) => setData((prev) => ({ ...prev, [field]: e.target.value }));

  async function searchZip(value) {
    const digits = String(value || "").replace(/\D/g, "");
    if (digits.length !== 8 || digits === lastZipRef.current) return;
    lastZipRef.current = digits;
    setZipLoading(true);
    const address = await toolsService.searchForCep(digits);
    setZipLoading(false);
    if (!address) {
      setZipError("CEP não encontrado. Preencha o endereço.");
      return;
    }
    setZipError("");
    setData((prev) => ({
      ...prev,
      address: address.logradouro || prev.address,
      location: address.bairro || prev.location,
      city: address.localidade || prev.city,
      state: address.uf || prev.state,
    }));
  }

  async function save(body, onSaved) {
    setSaving(true);
    // O service mostra o toast do erro (a mensagem do back)
    const result = await companyService.save(body, companyId);
    setSaving(false);
    if (!result || result.error) return;
    onSaved(result.data || {});
  }

  async function saveData() {
    setDataSubmitted(true);
    const errors = validateCompanyData(data, saved.data);
    if (Object.keys(errors).length) {
      focusFirstError(DATA_FIELDS.filter((id) => errors[id]));
      return;
    }
    const body = companyPayload(saved, { data });
    await save(body, (response) => {
      const company = companyFromApi({
        ...response,
        logos: response.logos || saved.logos,
        parameters: body.parameters,
        colors: saved.savedColors,
      });
      setSaved(company);
      setData(company.data);
      lastZipRef.current = company.data.zip_code.replace(/\D/g, "");
      setDataSubmitted(false);
      setZipError("");
      updateCompanyData({ name: body.name, cnpj: response.cnpj, parameters: body.parameters });
      toast.success("Dados da empresa salvos");
    });
  }

  async function saveBrand() {
    setColorsSubmitted(true);
    const errors = validateColors(colors);
    if (Object.keys(errors).length) {
      focusFirstError(Object.keys(errors));
      return;
    }
    const body = companyPayload(saved, { colors });
    try {
      if (logos.small?.file) body.smallLogoData = await readFileAsDataUrl(logos.small.file);
      if (logos.normal?.file) body.normalLogoData = await readFileAsDataUrl(logos.normal.file);
    } catch (err) {
      toast.error("Não foi possível ler a imagem. Escolha o arquivo de novo.");
      return;
    }
    await save(body, (response) => {
      const nextLogos = response.logos || saved.logos;
      const company = { ...saved, colors: companyFromApi({ colors: body.colors }).colors, savedColors: body.colors, logos: nextLogos };
      setSaved(company);
      setColors(company.colors);
      resetLogos();
      setColorsSubmitted(false);
      updateCompanyData({ colors: body.colors, logos: nextLogos });
      toast.success("Logo e cores salvos. O app já está com as cores novas.");
    });
  }

  function discard() {
    if (tab.value === "data") {
      setData(saved.data);
      setDataSubmitted(false);
      setZipError("");
      lastZipRef.current = saved.data.zip_code.replace(/\D/g, "");
      setCheckboxKey((key) => key + 1);
    } else {
      setColors(saved.colors);
      resetLogos();
      setColorsSubmitted(false);
    }
  }

  if (loading) return <FormPageSkeleton sections={[48, 190, 250, 120]} />;
  if (!saved) return null;

  const isData = tab.value === "data";
  const dirty = isData ? dataDirty : brandDirty;
  const smallSrc = saved.logos.small_logo ? apiFileUrl(saved.logos.small_logo) : undefined;
  const normalSrc = saved.logos.normal_logo ? apiFileUrl(saved.logos.normal_logo) : undefined;
  // Na prévia, o logo escolhido e ainda não salvo; sem reduzido, o completo, como no Sidebar
  const previewLogo = logos.small?.preview || smallSrc || logos.normal?.preview || normalSrc;

  return (
    <FormPage
      notice={<SelectorPageStatus $variant="segmented" options={TABS} selected={tab} onChange={setTab} />}
      actions={
        <PageActions>
          {dirty ? <ButtonLink $text="Descartar alterações" $action={discard} /> : null}
          <Button
            $text={isData ? "Salvar dados" : "Salvar logo e cores"}
            $variant="primary"
            $action={isData ? saveData : saveBrand}
            $isLoading={saving}
            $isDisabled={!dirty}
          />
        </PageActions>
      }
    >
      {isData ? (
        <>
          <FormBox title="Empresa">
            <Row $gap={20}>
              <Column $columns={6}>
                <InputText
                  $idForm="company-name"
                  $label="Nome da empresa"
                  $variant="default"
                  $errors={dataErrors["company-name"]}
                  value={data.name}
                  onChange={setField("name")}
                />
              </Column>
              <Column $columns={3}>
                <InputCnpj
                  $idForm="company-cnpj"
                  $label="CNPJ"
                  $variant="default"
                  $errors={dataErrors["company-cnpj"]}
                  value={data.cnpj}
                  onChange={setField("cnpj")}
                />
              </Column>
              <Column $columns={3}>
                <InputPhoneNumber
                  $idForm="company-phone"
                  $label="Telefone"
                  $variant="default"
                  $errors={dataErrors["company-phone"]}
                  value={data.phone}
                  onChange={setField("phone")}
                />
              </Column>
            </Row>
          </FormBox>

          <FormBox title="Endereço">
            <Row $gap={20}>
              <Column $columns={3}>
                <InputCEP
                  $idForm="company-zip"
                  $label="CEP"
                  $variant="default"
                  $hasTooltip="Com o CEP completo, o endereço é preenchido sozinho."
                  $errors={dataErrors["company-zip"] || zipError}
                  placeholder={zipLoading ? "Buscando o endereço…" : undefined}
                  value={data.zip_code}
                  onChange={(e) => {
                    const { value } = e.target;
                    setData((prev) => ({ ...prev, zip_code: value }));
                    if (zipError) setZipError("");
                    searchZip(value);
                  }}
                  onBlur={(e) => searchZip(e.target.value)}
                />
              </Column>
              <Column $columns={9}>
                <InputText
                  $idForm="company-address"
                  $label="Endereço"
                  $variant="default"
                  placeholder="Ex.: Rua das Flores"
                  value={data.address}
                  onChange={setField("address")}
                />
              </Column>
            </Row>
            <Row $gap={20}>
              <Column $columns={3}>
                <InputText
                  $idForm="company-number"
                  $label="Número"
                  $variant="default"
                  value={data.number}
                  onChange={setField("number")}
                />
              </Column>
              <Column $columns={3}>
                <InputText
                  $idForm="company-location"
                  $label="Bairro"
                  $variant="default"
                  value={data.location}
                  onChange={setField("location")}
                />
              </Column>
              <Column $columns={3}>
                <InputText
                  $idForm="company-city"
                  $label="Cidade"
                  $variant="default"
                  value={data.city}
                  onChange={setField("city")}
                />
              </Column>
              <Column $columns={3}>
                <InputText
                  $idForm="company-state"
                  $label="Estado"
                  $variant="default"
                  placeholder="Ex.: PR"
                  value={data.state}
                  onChange={setField("state")}
                />
              </Column>
            </Row>
          </FormBox>

          <FormBox title="Declarações">
            <FullWidth>
              <Stack>
                <InputCheckbox
                  key={checkboxKey}
                  $options={[{ name: "company-anonymous", label: "Aceitar declarações anônimas", value: data.permitAnonymous }]}
                  onChange={(e) =>
                    e.target.name === "company-anonymous" &&
                    setData((prev) => ({ ...prev, permitAnonymous: e.target.checked }))
                  }
                />
                <Text
                  $text="Se marcado, os colaboradores podem enviar declarações sem se identificar. A empresa e os colegas veem a declaração, mas ninguém vê quem enviou."
                  $variant="caption_medium"
                  $color="N400"
                />
              </Stack>
            </FullWidth>
          </FormBox>
        </>
      ) : (
        <>
          <Notice $variant="blue" $text="O logo e as cores valem para todos da empresa: menu, botões, links e abas." />
          <Row $gap={20}>
            <Column $columns={7}>
              <Stack>
                <FormBox title="Logo">
                  <Row $gap={20}>
                    <Column $columns={6}>
                      <LogoField
                        id="logo-small"
                        label="Logo reduzido"
                        hint="Aparece no topo do menu lateral. Sugestão: 128×128, fundo transparente."
                        kind="symbol"
                        currentSrc={smallSrc}
                        companyName={saved.data.name}
                        logo={logos.small}
                        onChange={(logo) => changeLogo("small", logo)}
                      />
                    </Column>
                    <Column $columns={6}>
                      <LogoField
                        id="logo-normal"
                        label="Logo completo"
                        hint="Usado no menu quando não há o reduzido. Sugestão: 560×112, fundo transparente."
                        kind="full"
                        currentSrc={normalSrc}
                        companyName={saved.data.name}
                        logo={logos.normal}
                        onChange={(logo) => changeLogo("normal", logo)}
                      />
                    </Column>
                  </Row>
                </FormBox>

                <FormBox title="Cores">
                  <Row $gap={20}>
                    <Column $columns={6}>
                      <ColorField
                        id="color-primary"
                        label="Cor primária"
                        tooltip="Botões, links, abas e o contorno dos campos. Vazia, fica o azul OneWork."
                        value={colors.primary}
                        error={colorErrors["color-primary"]}
                        onChange={(value) => setColors((prev) => ({ ...prev, primary: value }))}
                      />
                    </Column>
                    <Column $columns={6}>
                      <ColorField
                        id="color-secondary"
                        label="Cor secundária"
                        tooltip="Usada nos botões quando a primária é verde ou vermelha, que são as cores dos status."
                        value={colors.secondary}
                        error={colorErrors["color-secondary"]}
                        onChange={(value) => setColors((prev) => ({ ...prev, secondary: value }))}
                      />
                    </Column>
                  </Row>
                </FormBox>
              </Stack>
            </Column>
            <Column $columns={5}>
              <FormBox title="Prévia">
                <FullWidth>
                  <ThemePreview colors={colors} logoSrc={previewLogo} companyName={saved.data.name} />
                </FullWidth>
              </FormBox>
            </Column>
          </Row>
        </>
      )}
    </FormPage>
  );
}
